<script setup lang="ts">
import {
  editorDisplayStates,
  findDisplayState,
  variantStateId,
} from "./editorResolve";
import { getNavigationPageDisplayStateReferences } from "../../../lib/navigationPageState";
import { summarizeSetFields } from "./fieldRegistry";
import NavigationDisplayStatesDialog from "./NavigationDisplayStatesDialog.vue";
import NavigationDisplayStatesRawDrawer from "./NavigationDisplayStatesRawDrawer.vue";
import NavigationLinkDialog from "./NavigationLinkDialog.vue";
import NavigationVariantDialog from "./NavigationVariantDialog.vue";
import MiniLayerRow from "./MiniLayerRow.vue";

type ActiveDialog =
  | { kind: "link"; itemIndex: number }
  | { kind: "variant"; itemIndex: number; variantIndex: number }
  | { kind: "states"; stateId?: string }
  | { kind: "states-raw" };

const { t } = useI18n({ useScope: "local" });
const props = defineProps<{ id: string; defaultOpen?: boolean }>();

const { field, block, addItem, removeItem, updateItem, patch } = useInlineBlockEditor(props.id);

const scale = field<"sm" | "md" | "lg" | "xl">("scale", "md");
const items = computed<any[]>(() => Array.isArray(block.value?.items) ? block.value.items : []);
const displayStates = computed(() => editorDisplayStates(block.value?.displayStates));
const defaultDisplayStateId = computed(
  () => displayStates.value.find((state) => state.id === "default")?.id
    ?? displayStates.value[0]?.id
    ?? "default",
);
const scaleOptions = ["sm", "md", "lg", "xl"] as const;
const activeDialog = shallowRef<ActiveDialog | null>(null);

function updateScale(value: unknown) {
  scale.value = value as "sm" | "md" | "lg" | "xl";
}

function stateName(stateId: string | undefined): string {
  return findDisplayState(displayStates.value, stateId).name;
}

function stateDotClass(stateId: string | undefined) {
  if (stateId === "current") return "border-primary/40 bg-primary";
  if (stateId === "completed") return "border-success/40 bg-success";
  if (stateId === "locked") return "border-border bg-muted";
  return "border-muted-foreground/30 bg-muted";
}

function stateSwatchClasses(stateId: string | undefined) {
  return findDisplayState(displayStates.value, stateId).classes?.card || "bg-background";
}

function openLink(itemIndex: number) {
  activeDialog.value = { kind: "link", itemIndex };
}

function openVariant(itemIndex: number, variantIndex: number) {
  activeDialog.value = { kind: "variant", itemIndex, variantIndex };
}

function openStates(stateId?: string) {
  activeDialog.value = { kind: "states", stateId };
}

function openRawStates() {
  activeDialog.value = { kind: "states-raw" };
}

function closeDialog() {
  activeDialog.value = null;
}

function addLink() {
  const nextIndex = items.value.length;
  addItem("items", {
    id: crypto.randomUUID(),
    title: "",
    description: "",
    icon: "lucide:circle-arrow-right",
    image: "",
    url: "",
    to: "",
    displayStateId: defaultDisplayStateId.value,
    classes: {},
    variants: [],
  });
  activeDialog.value = { kind: "link", itemIndex: nextIndex };
}

function variantsFor(item: any) {
  return Array.isArray(item?.variants) ? item.variants : [];
}

function addVariant(itemIndex: number) {
  const item = items.value[itemIndex];
  if (!item) return;
  const variants = variantsFor(item);
  updateItem("items", itemIndex, {
    variants: [
      ...variants,
      {
        id: crypto.randomUUID(),
        name: t("links.variantDefaultName", { number: variants.length + 1 }),
        composition: "all",
        conditions: [],
        displayStateId: defaultDisplayStateId.value,
        classes: {},
      },
    ],
  });
  activeDialog.value = { kind: "variant", itemIndex, variantIndex: variants.length };
}

function addDisplayState() {
  const newState = {
    id: crypto.randomUUID(),
    name: t("states.defaultName", { number: displayStates.value.length + 1 }),
    classes: {},
  };
  patch({ displayStates: [...displayStates.value, newState] });
  activeDialog.value = { kind: "states", stateId: newState.id };
}

function usageCount(stateId: string) {
  const count = getNavigationPageDisplayStateReferences(items.value, stateId).length;
  return count === 1
    ? t("states.oneUse")
    : t("states.manyUses", { count });
}

function fieldSummary(item: any) {
  return summarizeSetFields(item);
}

function handleVariantBack() {
  if (activeDialog.value?.kind !== "variant") return;
  activeDialog.value = {
    kind: "link",
    itemIndex: activeDialog.value.itemIndex,
  };
}
</script>

<template>
  <EditorSection
    :title="t('title')"
    icon="lucide:panel-top-open"
    accent="navigation"
    :default-open="props.defaultOpen ?? true"
  >
    <EditorField :label="t('fields.scale.label')" :hint="t('fields.scale.hint')">
      <Select :model-value="scale" @update:model-value="updateScale">
        <SelectTrigger class="h-8 rounded-sm bg-muted text-[13px]">
          <SelectValue :placeholder="t('fields.scale.placeholder')" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="option in scaleOptions" :key="option" :value="option">
            {{ t(`fields.scale.options.${option}`) }}
          </SelectItem>
        </SelectContent>
      </Select>
    </EditorField>

    <EditorField :label="t('links.label')">
      <p class="text-[11.5px] text-muted-foreground">
        {{ t("links.hint") }}
      </p>

      <div class="mt-2 space-y-2">
        <div
          v-for="(item, index) in items"
          :key="item.id ?? index"
          class="overflow-hidden rounded-lg border bg-card"
        >
          <div class="flex items-stretch border-b">
            <button
              type="button"
              class="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left hover:bg-[color-mix(in_oklch,var(--cat-navigation)_5%,transparent)]"
              @click="openLink(index)"
            >
              <span :class="['size-2.5 shrink-0 rounded-full border', stateDotClass(item.displayStateId)]" />
              <span class="min-w-0 flex-1 truncate text-[12.5px] font-semibold">
                {{ item.title || t('links.untitled') }}
              </span>
              <span class="inline-flex items-center gap-1 text-[10px] font-semibold text-muted-foreground">
                <Icon mode="svg" size="11" name="lucide:pencil" />
                {{ t("links.edit") }}
              </span>
            </button>
            <button
              type="button"
              class="grid w-8 place-items-center border-l text-muted-foreground hover:text-destructive"
              @click="removeItem('items', index)"
            >
              <Icon mode="svg" size="13" name="lucide:trash-2" />
            </button>
          </div>

          <div class="flex flex-col gap-1.5 p-2">
            <MiniLayerRow
              chip="BASE"
              tone="base"
              :title="stateName(item.displayStateId)"
              :meta="t('links.sharedLook')"
              :dot="stateDotClass(item.displayStateId)"
              @click="openLink(index)"
            />
            <MiniLayerRow
              chip="LINK"
              tone="link"
              class="ml-2.5"
              :title="fieldSummary(item)"
              @click="openLink(index)"
            />
            <MiniLayerRow
              v-for="(variant, variantIndex) in variantsFor(item)"
              :key="variant.id"
              :chip="`V${variantIndex + 1}`"
              tone="variant"
              class="ml-5"
              :title="variant.name || t('links.variantUntitled', { number: variantIndex + 1 })"
              :meta="stateName(variantStateId(variant))"
              :dot="stateDotClass(variantStateId(variant))"
              @click="openVariant(index, variantIndex)"
            />
            <div
              v-if="!variantsFor(item).length"
              class="ml-5 rounded-md border border-dashed px-2 py-1.5 text-[10.5px] text-muted-foreground"
            >
              {{ t("links.noVariants") }}
            </div>
            <Button
              type="button"
              variant="outline"
              class="ml-5 h-[26px] justify-center rounded-md border-dashed text-[10.5px] font-semibold"
              @click="addVariant(index)"
            >
              <Icon mode="svg" size="11" name="lucide:plus" class="mr-1" />
              {{ t("links.addVariant") }}
            </Button>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          class="w-full rounded-sm text-[13px]"
          @click="addLink"
        >
          <Icon mode="svg" size="16" name="lucide:plus" class="mr-1.5" />
          {{ t("links.add") }}
        </Button>
      </div>
    </EditorField>
  </EditorSection>

  <EditorSection
    :title="t('states.title')"
    icon="lucide:swatch-book"
    accent="navigation"
    :default-open="false"
  >
    <p class="text-[11.5px] text-muted-foreground">
      {{ t("states.hint") }}
    </p>

    <div class="mt-2 space-y-1.5">
      <button
        v-for="state in displayStates"
        :key="state.id"
        type="button"
        class="flex w-full items-center gap-2 rounded-md border bg-card px-2.5 py-2 text-left hover:border-[var(--cat-navigation)]"
        @click="openStates(state.id)"
      >
        <span :class="['size-[18px] shrink-0 rounded-md border', stateSwatchClasses(state.id)]" />
        <span class="min-w-0 flex-1">
          <span class="block truncate text-[12.5px] font-semibold">{{ state.name }}</span>
          <span class="block truncate text-[10.5px] text-muted-foreground">
            {{ usageCount(state.id) }}
          </span>
        </span>
        <Icon mode="svg" size="14" name="lucide:pencil" class="shrink-0 text-muted-foreground" />
      </button>

      <Button
        type="button"
        variant="outline"
        class="h-8 w-full rounded-md border-dashed text-[11px] font-semibold"
        @click="addDisplayState"
      >
        <Icon mode="svg" size="12" name="lucide:plus" class="mr-1" />
        {{ t("states.add") }}
      </Button>

      <Button
        type="button"
        variant="outline"
        class="h-8 w-full justify-start rounded-md text-[11px] font-semibold"
        @click="openRawStates"
      >
        <Icon mode="svg" size="12" name="lucide:curly-braces" class="mr-1.5" />
        {{ t("states.raw") }}
      </Button>
    </div>
  </EditorSection>

  <NavigationLinkDialog
    v-if="activeDialog?.kind === 'link'"
    :block-id="props.id"
    :item-index="activeDialog.itemIndex"
    :open="true"
    @update:open="(open) => !open && closeDialog()"
    @open-variant="(variantIndex) => openVariant(activeDialog.itemIndex, variantIndex)"
  />

  <NavigationVariantDialog
    v-if="activeDialog?.kind === 'variant'"
    :block-id="props.id"
    :item-index="activeDialog.itemIndex"
    :variant-index="activeDialog.variantIndex"
    :open="true"
    @update:open="(open) => !open && closeDialog()"
    @back="handleVariantBack"
  />

  <NavigationDisplayStatesDialog
    v-if="activeDialog?.kind === 'states'"
    :block-id="props.id"
    :open="true"
    :initial-state-id="activeDialog.stateId"
    @update:open="(open) => !open && closeDialog()"
  />

  <NavigationDisplayStatesRawDrawer
    v-if="activeDialog?.kind === 'states-raw'"
    :block-id="props.id"
    :open="true"
    @update:open="(open) => !open && closeDialog()"
  />
</template>

<i18n lang="yaml">
en:
  title: "Navigation Page"
  fields:
    scale:
      label: "Scale"
      placeholder: "Select a scale"
      hint: "Increases the title, description, and icon sizing to make cards feel bolder."
      options:
        sm: "Small"
        md: "Medium"
        lg: "Large"
        xl: "Extra Large"
  links:
    label: "Navigation link(s)"
    hint: "Each row below is a layer of the final card — click one to edit it in a dialog. We usually keep one link per block and lay blocks out in a grid."
    add: "Add link"
    addVariant: "Add variant"
    edit: "Edit"
    untitled: "Untitled link"
    variantUntitled: "Variant {number}"
    variantDefaultName: "State {number}"
    noVariants: "No variants — always shows the base look."
    sharedLook: "shared look"
  states:
    title: "Display states"
    hint: "Reusable looks that items and variants reference by name."
    add: "Add display state"
    raw: "Edit raw display states..."
    defaultName: "State {number}"
    oneUse: "1 use"
    manyUses: "{count} uses"
</i18n>
